import { FC, FormEvent, useRef, useState } from 'react';
import s from './AddKeywordsModal.module.scss';
import { DropdownItem, Subtitle, Title } from '@tremor/react';
import { ZodError, z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TagsSelection } from '@components/layouts/Modals/TagsSelection/TagsSelection';
import Papa, { ParseResult } from 'papaparse';
import { collection, getDocs, query } from 'firebase/firestore';
import { flatten } from 'lodash';
import { GeneralModalProps, SelectionTag } from '@/types/modals';
import { KeywordFromStore, RawKeywordCSVData, TagFromStore } from '@/types/types';
import { ModalWrapper } from '../ModalWrapper/ModalWrapper';
import { useKeywordsStore } from '@/stores/keywordsStore';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { FormValues, SearchBar } from '../../Inputs/SearchBar/SearchBar';
import { KeywordsService } from '@/services/dataforseo/keywordsService';
import { keywordCSVSchema } from '@/utils/schemas';
import { db } from '@/firebase/init';
import { getWorkspaceTagsPath } from '@/utils/queryPaths';
import { OutlinedButton } from '../../Buttons/OutlinedButton/OutlinedButton';
import { OutlinedSelectInput } from '../../Inputs/OutlinedSelectInput/OutlinedSelectInput';
import { ModalBottomActions } from '../ModalBottomActions/ModalBottomActions';
import { ImportIcon } from '@/assets';
import all_countries_raw from '@/assets/csv/language_codes.csv';
import { showToast } from '@/utils/toasts';
import { hasPermissionMessage } from '@/auth/hasPermission';

const all_countries = all_countries_raw as { alpha2: string }[];
all_countries.sort((a, b) => a['alpha2'].localeCompare(b['alpha2']));
interface Props extends GeneralModalProps {
  tags: TagFromStore[];
  locationCode: number;
  isOnlyFirstPageFetched: boolean;
}

const keywordsSchema = z.object({
  keywords: z.string(),
  language: z.literal('en').or(z.literal('de')).or(z.literal('fr')),
});

type Keywords = z.infer<typeof keywordsSchema>;

export const AddKeywordsModal: FC<Props> = ({
  isOpen,
  setClose,
  tags,
  locationCode,
  isOnlyFirstPageFetched,
}) => {
  return (
    <ModalWrapper
      className={s.addModalContainer}
      isOpen={isOpen}
      onRequestClose={setClose}
    >
      <AddKeywordsModalContent
        isOnlyFirstPageFetched={isOnlyFirstPageFetched}
        tags={tags}
        setClose={setClose}
        locationCode={locationCode}
      />
    </ModalWrapper>
  );
};

const AddKeywordsModalContent: FC<
  Pick<Props, 'tags' | 'setClose' | 'locationCode' | 'isOnlyFirstPageFetched'>
> = ({ tags, setClose, locationCode, isOnlyFirstPageFetched }) => {
  const [selectionTags, setSelectionTags] = useState<SelectionTag[]>(() => {
    return tags.map((tag) => ({ ...tag, isSelected: false }));
  });
  const setKeywords = useKeywordsStore((state) => state.setKeywords);
  const currentKeywords = useKeywordsStore((state) => state.keywords);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const displayTags =
    searchValue === ''
      ? selectionTags
      : selectionTags.filter((tag) =>
          tag.name.toLowerCase().includes(searchValue.toLowerCase()),
        );
  const { register, handleSubmit, control } = useForm<Keywords>({
    resolver: zodResolver(keywordsSchema),
    defaultValues: { keywords: '', language: 'en' },
  });
  const workspaceId = useWorkspaceIdFromParams() as string;
  const userId = useUserPermissionsStore((state) => state.userId);

  const handleTagClick = (tagId: string) => {
    const tagIndex = selectionTags.findIndex((tag) => tag.id === tagId);
    const tagCopy = { ...selectionTags[tagIndex] };
    tagCopy.isSelected = !tagCopy.isSelected;
    const tagsCopy = [...selectionTags];
    tagsCopy[tagIndex] = tagCopy;
    setSelectionTags(tagsCopy);
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const target = e.target as typeof e.target & FormValues;
    const searchValue = target.search.value;
    setSearchValue(searchValue);
  };

  const handleFormSubmit = async (data: Keywords) => {
    setIsLoading(true);
    const tags = selectionTags.filter((tag) => tag.isSelected).map((tag) => tag.id);
    const keywords = data.keywords;

    const rawKeywords = flatten(
      keywords.split('\n').map((keyword) => keyword.split(',')),
    );
    const parsedKeywords = rawKeywords
      .map((keywords) => keywords.trim().toLowerCase())
      .filter((keywords) => keywords.length !== 0);

    const language = data.language;

    const [, message] = hasPermissionMessage('workspace', 'keyword', 'create', {
      numOfAddingKeywords: parsedKeywords.length,
      numOfExistingKeywords: currentKeywords.length,
    });

    try {
      if (message) {
        showToast('Cannot add more keywords!', message, 'error');
        throw new Error('Max keywords num reached');
      }

      await KeywordsService.createKeywords(
        parsedKeywords,
        language,
        tags,
        workspaceId,
        userId as string,
        'dataforseo',
        locationCode,
      );
      let newKeywords: KeywordFromStore[];
      if (isOnlyFirstPageFetched) {
        newKeywords = await KeywordsService.getFirstPage(workspaceId, 'dataforseo');
      } else {
        newKeywords = await KeywordsService.getAllKeywords(workspaceId, 'dataforseo');
      }
      setKeywords(newKeywords);
      setClose();
    } catch (e) {
      console.error(e);
      return { error: (e as object).toString() };
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteParser = async (results: ParseResult<RawKeywordCSVData>) => {
    setIsLoading(true);

    try {
      keywordCSVSchema.parse(results.data);
      const [, message] = hasPermissionMessage('workspace', 'keyword', 'create', {
        numOfAddingKeywords: results.data.length,
        numOfExistingKeywords: currentKeywords.length,
      });

      if (message) {
        showToast('Cannot add more keywords!', message, 'error');
        throw new Error('Max keywords num reached');
      }
      const tagsSnapshot = await getDocs(
        query(collection(db, getWorkspaceTagsPath(workspaceId, 'dataforseo'))),
      );
      // get the most current tags from store so there's less chance for a mismatch
      const tags = tagsSnapshot.docs.map(
        (doc) => ({ ...doc.data(), id: doc.id } as TagFromStore),
      );
      await KeywordsService.createKeywordsFromCSVData(
        results,
        tags,
        workspaceId,
        userId as string,
        'dataforseo',
        locationCode,
      );
      let newKeywords: KeywordFromStore[];
      if (isOnlyFirstPageFetched) {
        newKeywords = await KeywordsService.getFirstPage(workspaceId, 'dataforseo');
      } else {
        newKeywords = await KeywordsService.getAllKeywords(workspaceId, 'dataforseo');
      }
      setKeywords(newKeywords);
    } catch (e) {
      const error = e as ZodError;
      console.error(error);
      showToast('Error!', error.errors[0].message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileImport = (e: FormEvent<HTMLInputElement>) => {
    const fileReader = new FileReader();
    const file = e.currentTarget.files?.[0];
    if (file) {
      fileReader.readAsText(file);
      Papa.parse<RawKeywordCSVData>(file, {
        header: true,
        skipEmptyLines: true,
        complete: handleCompleteParser,
      });
    }
  };
  const options = all_countries.map((val) => (
    <DropdownItem
      key={val['alpha2']}
      value={val['alpha2']}
      text={val['alpha2'].toUpperCase()}
    />
  ));
  return (
    <>
      <section className={s.upperContent}>
        <Title className={s.title}>Add Keywords</Title>
        <input
          onChange={handleFileImport}
          type="file"
          ref={uploadFileInputRef}
          hidden
          accept="text/csv"
        />
        <OutlinedButton
          onClick={() => {
            if (uploadFileInputRef.current) {
              uploadFileInputRef.current.click();
            }
          }}
          icon={ImportIcon}
          iconAltText="import keywords button icon"
        >
          Import CSV
        </OutlinedButton>
      </section>
      <form onSubmit={handleSearchSubmit} id="add-keywords-tag-search-modal-form"></form>
      <form onSubmit={handleSubmit(handleFormSubmit)} id="add-keywords-modal-form"></form>
      <div className={s.mainForm}>
        <textarea
          {...register('keywords')}
          disabled={isLoading}
          className={s.textarea}
          form="add-keywords-modal-form"
        ></textarea>
        <section className={s.tagsOptions}>
          <Subtitle className={s.subtitle}>Assign Tags</Subtitle>
          <Controller
            control={control}
            name="language"
            render={({ field: { ...rest } }) => (
              <OutlinedSelectInput
                disabled={isLoading}
                {...rest}
                defaultValue="en"
                className={s.select}
              >
                {options}
              </OutlinedSelectInput>
            )}
          />
          <SearchBar
            placeholder="Find Tag"
            insideForm={false}
            form="add-keywords-tag-search-modal-form"
          />
        </section>
        <TagsSelection
          handleTagClick={handleTagClick}
          displayTags={displayTags}
          isLoading={isLoading}
        />
        <ModalBottomActions
          setClose={setClose}
          isLoading={isLoading}
          form="add-keywords-modal-form"
        />
      </div>
    </>
  );
};
